import { BackLink } from '../../components/back-link';
import sharedStyles from '../shared.module.css';
import { useBankIdCheck } from './hooks/check.hook';
import { ApiError, BankIdService, InitResponse } from '@lhb/api/idp';
import { Akka, Button, cn } from '@lhb/ui';
import { Case, convertCase } from '@lhb/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useLoaderData } from 'react-router-dom';
import { MessageBlock } from '../../components/blocks';
import { appInsights } from '../../../appinsight';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { QRCodeDialog } from '../../components/qr-dialog/qr-dialog';
import { useCountdownProgress } from './hooks/count-down.hook';
import { Progress } from '../../components/progress-bar/progress-bar';
import { getProgressMessage } from '../../helpers/progress-message';
import { ScreenReaderAnnouncer } from '../../components/screen-reader-announcer/screen-reader-announcer';

export async function BankIdOtherLoader() {
  try {
    console.log('Initiating BankId process');
    appInsights?.trackTrace({
      severityLevel: SeverityLevel.Information,
      message: 'Initiate BankId process',
    });
    const init = await BankIdService.init({ sameDevice: false });

    return init;
  } catch (e) {
    console.log('BankId Init error', e);
    appInsights?.trackException({
      error: e as Error,
      severityLevel: SeverityLevel.Error,
      properties: { loader: 'BankId Init error' },
    });
    if (e instanceof ApiError) {
      throw new Response(e.message, {
        status: e.status,
        statusText: e.statusText,
      });
    }

    throw new Response('Internal Error', {
      status: 500,
      statusText: e as string,
    });
  }
}

export function BankIdOther() {
  const { t } = useTranslation('BANKID_OTHER');
  const { requestId } = useLoaderData() as InitResponse;
  const { status, hintCode, qrCode, state } = useBankIdCheck(requestId);
  const { remainingMs, progressPercent, isDone, showRefresh, reset } =
    useCountdownProgress(6 * 60 * 1000);
  const [open, setOpen] = useState(false);
  const isUserSign = useMemo(
    () => status && ['UserSign', 'Complete'].includes(status),
    [status],
  );
  const dialogRef = useRef<HTMLDialogElement>(null);
  const refreshBtnRef = useRef<HTMLButtonElement>(null);
  const closeQrDialogBtnRef = useRef<HTMLButtonElement>(null);
  const dialogRefreshBtnRef = useRef<HTMLButtonElement>(null);
  const dialogBtnRef = useRef<HTMLButtonElement>(null);

  const openDialog = () => {
    const doOpen = () => {
      if (!dialogRef.current) return;
      setOpen(true);
      requestAnimationFrame(() => {
        dialogRef.current?.showModal();
      });
    };
    if ('startViewTransition' in document) {
      document.startViewTransition(doOpen);
    } else {
      doOpen();
    }
  };

  useEffect(() => {
    dialogBtnRef.current?.focus();
  }, [dialogBtnRef.current]);

  const closeDialog = () => {
    const doClose = () => {
      if (!dialogRef.current) return;
      dialogRef.current.close();
      setOpen(false);
    };
    if ('startViewTransition' in document) {
      document.startViewTransition(doClose);
    } else {
      doClose();
    }
  };

  const resetCounter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    reset();
    if (dialogBtnRef.current) dialogBtnRef.current.focus();
  };

  useEffect(() => {
    if (isDone) {
      closeDialog();
      throw new Error('EXPIREDTRANSACTION');
    }
  }, [isDone]);

  useEffect(() => {
    if (showRefresh) {
      if (open) dialogRefreshBtnRef.current?.focus();
      else refreshBtnRef.current?.focus();
    } else {
      if (open) closeQrDialogBtnRef.current?.focus();
      else dialogBtnRef.current?.focus();
    }
  }, [showRefresh]);

  useEffect(() => {
    if (!dialogRef.current) return;
    if (!isUserSign) return;

    dialogRef.current.close();
  }, [dialogRef, isUserSign]);

  if (state === 'failed') {
    console.log('BankId failed with hintCode:', hintCode);
    throw new Error(hintCode);
  }

  return (
    <>
      <BackLink asChild label="Avbryt">
        <Button
          variant="tertriary"
          className={cn(sharedStyles['close'], sharedStyles['hide-mobile'])}
          trailingIcon="cross-small"
        />
      </BackLink>
      <h1 className={sharedStyles['heading']}>
        {t('LOGIN_INTERNETBANK_TITLE')}
      </h1>
      <MessageBlock>
        {status && (
          <p className={cn(sharedStyles['subtitle'])}>
            {t(`BANKID_OTHER:${convertCase(status, Case.Snake).toUpperCase()}`)}
          </p>
        )}
      </MessageBlock>
      {qrCode && !isUserSign ? (
        <>
          <div className={sharedStyles['qr-code-wrapper']}>
            <button
              ref={dialogBtnRef}
              onClick={openDialog}
              className={sharedStyles['qr-code-button']}
              aria-label={t('QR_CODE_ARIA_LABEL')}
            >
              <QRCode className={sharedStyles['qr-code']} value={qrCode} />
            </button>
            <>
              <Progress progress={progressPercent}>
                <p className={sharedStyles['progress-text']}>
                  {getProgressMessage(remainingMs, t)}
                </p>
                <Button
                  ref={refreshBtnRef}
                  className={
                    !showRefresh ? sharedStyles['hide-refresh-button'] : ''
                  }
                  onClick={resetCounter}
                >
                  {t('REFRESH')}
                </Button>
              </Progress>
            </>
          </div>

          <QRCodeDialog
            closeBtnRef={closeQrDialogBtnRef}
            onClose={closeDialog}
            ref={dialogRef}
          >
            {open && (
              <QRCode
                className={sharedStyles['dialog-qrcode']}
                value={qrCode}
              />
            )}
            <>
              <Progress progress={progressPercent}>
                <p className={sharedStyles['progress-text']}>
                  {getProgressMessage(remainingMs, t)}
                </p>
                <Button
                  ref={dialogRefreshBtnRef}
                  className={
                    !showRefresh ? sharedStyles['hide-refresh-button'] : ''
                  }
                  onClick={resetCounter}
                >
                  {t('REFRESH')}
                </Button>
              </Progress>
            </>
          </QRCodeDialog>

          <ScreenReaderAnnouncer
            message={getProgressMessage(remainingMs, t)}
            ariaLive="polite"
          />
        </>
      ) : (
        <>
          <Akka className={sharedStyles['loader']} />
        </>
      )}
      <BackLink asChild label="Avbryt">
        <Button
          variant="tertriary"
          className={cn(sharedStyles['close'], sharedStyles['hide-desktop'])}
          trailingIcon="cross-small"
        />
      </BackLink>
    </>
  );
}
